import UserPage from 'flarum/forum/components/UserPage';
import app from 'flarum/forum/app';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

/**
 * MentorPage — 师生关系（UserPage 子页面）
 *
 * 路由：/u/{username}/mentors
 * 显示在个人主页右侧内容区，保持左侧导航栏
 *
 * Tab:
 *   我的导师 → 当前导师信息 + 拜师时间 + 解除关系 + 曾经的导师历史
 *   我的学生 → 当前学生数量 + 学生档案列表（成为时长/活跃状态）+ 导师待办
 *
 * 等级卡（导师等级 C / 学员等级 S）数据来自 /taro/credits/{userId}
 */
export default class MentorPage extends UserPage {
  oninit(vnode) {
    super.oninit(vnode);

    this.tab = 'myMentor';
    this.loading = true;
    this.error = null;

    // 等级数据（学员等级 / 导师等级）
    this.creditData = null;

    // 数据
    this.mentors = [];
    this.myMentor = null;
    this.myMentorRelation = null;
    this.history = [];
    this.students = [];
    this.pendingRequests = [];
    this.pendingReviews = 0;
    this.pendingAnswers = 0;

    this.isMentor = false;

    // 支持两种路由：/u/:username/mentors 和旧版 /taro/mentors
    const username = m.route.param('username') || (app.session.user ? app.session.user.slug() : null);
    if (username) {
      this.loadUser(username);
    }
  }

  /** UserPage 加载完用户后调用 */
  show(user) {
    super.show(user);
    this.isMentor = !!(user && user.attribute('taroIsMentor'));
    this.loadData();
  }

  loadData() {
    this.loading = true;
    this.error = null;
    m.redraw();

    const api = app.forum.attribute('apiUrl');
    const userId = this.user ? this.user.id() : (app.session.user ? app.session.user.id() : null);

    const promises = [
      // 等级数据（学员等级 / 导师等级）
      app.request({ method: 'GET', url: api + '/taro/credits/' + userId }).then((r) => {
        const d = r.data || r;
        this.creditData = d.attributes || d;
      }).catch(() => { this.creditData = null; }),

      // 导师列表
      app.request({ method: 'GET', url: api + '/taro/mentors' }).then((r) => {
        this.mentors = Array.isArray(r) ? r : r.data || [];
      }).catch(() => { this.mentors = []; }),

      // 我的导师（当前）
      app.request({ method: 'GET', url: api + '/taro/mentors/my' }).then((r) => {
        const data = r.data || r;
        if (data && data.id) {
          this.myMentor = data;
          this.myMentorRelation = {
            id: r.meta?.relationId || null,
            approvedAt: r.meta?.approvedAt || null,
            createdAt: r.meta?.createdAt || null,
            status: 'approved',
          };
        } else {
          this.myMentor = null;
          this.myMentorRelation = null;
        }
      }).catch(() => {
        this.myMentor = null;
        this.myMentorRelation = null;
      }),

      // 师生档案（全部历史）
      app.request({ method: 'GET', url: api + '/taro/mentors/history' }).then((r) => {
        this.history = Array.isArray(r) ? r : r.data || [];
      }).catch(() => { this.history = []; }),

      // 我的学生（当前已批准）
      app.request({ method: 'GET', url: api + '/taro/mentors/students' }).then((r) => {
        this.students = Array.isArray(r) ? r : (r.data || []);
      }).catch(() => { this.students = []; }),
    ];

    // 导师额外数据
    if (this.isMentor) {
      promises.push(
        app.request({ method: 'GET', url: api + '/taro/mentors/pending-requests' }).then((r) => {
          this.pendingRequests = Array.isArray(r) ? r : r.data || [];
        }).catch(() => { this.pendingRequests = []; })
      );
      promises.push(
        app.request({ method: 'GET', url: api + '/taro/mentor/dashboard' }).then((r) => {
          const data = r.data || r;
          const attrs = data.attributes || data;
          this.pendingReviews = attrs.pending_reviews || 0;
          this.pendingAnswers = attrs.pending_answers || 0;
        }).catch(() => {})
      );
    }

    Promise.allSettled(promises).then(() => {
      this.loading = false;
      m.redraw();
    });
  }

  // ─── 拜师 ───────────────────────────────────────────

  requestMentor(mentor) {
    if (!this.user) return;
    if (!confirm(`确定向 ${this.userName(mentor)} 申请拜师？`)) return;

    const api = app.forum.attribute('apiUrl');
    this.sending = mentor.id();
    m.redraw();

    app.request({
      method: 'POST',
      url: api + '/taro/mentors/request',
      body: { mentor_id: mentor.id(), message: '' },
    }).then(() => {
      this.sending = null;
      alert('拜师申请已发送，等待导师确认！');
      this.loadData();
    }).catch((err) => {
      this.sending = null;
      alert('申请失败：' + (err.response?.errors?.[0]?.detail || err.statusText || '错误'));
      m.redraw();
    });
  }

  // ─── 批准/拒绝 ──────────────────────────────────────

  approveRequest(relationId) {
    const api = app.forum.attribute('apiUrl');
    this.actionLoading = relationId;
    m.redraw();

    app.request({
      method: 'POST', url: api + '/taro/mentors/' + relationId + '/approve',
    }).then(() => {
      this.actionLoading = null;
      this.pendingRequests = this.pendingRequests.filter((r) => r.id !== relationId);
      m.redraw();
    }).catch((err) => {
      this.actionLoading = null;
      alert('操作失败');
      m.redraw();
    });
  }

  rejectRequest(relationId) {
    if (!confirm('确定拒绝该拜师申请？')) return;
    const api = app.forum.attribute('apiUrl');
    this.actionLoading = relationId;
    m.redraw();

    app.request({
      method: 'POST', url: api + '/taro/mentors/' + relationId + '/reject',
    }).then(() => {
      this.actionLoading = null;
      this.pendingRequests = this.pendingRequests.filter((r) => r.id !== relationId);
      m.redraw();
    }).catch((err) => {
      this.actionLoading = null;
      alert('操作失败');
      m.redraw();
    });
  }

  // ─── 解除关系 ──────────────────────────────────────

  terminateRelation(relationId) {
    const reason = prompt('请输入解除原因（可选）：');
    if (reason === null) return;

    const api = app.forum.attribute('apiUrl');
    this.actionLoading = relationId;
    m.redraw();

    app.request({
      method: 'POST',
      url: api + '/taro/mentors/' + relationId + '/terminate',
      body: { reason: reason || 'manual' },
    }).then(() => {
      this.actionLoading = null;
      this.myMentor = null;
      this.myMentorRelation = null;
      alert('师生关系已解除');
      this.loadData();
    }).catch((err) => {
      this.actionLoading = null;
      alert('操作失败');
      m.redraw();
    });
  }

  // ─── 工具 ───────────────────────────────────────────

  userName(obj) {
    const a = obj.attributes || obj || {};
    return a.displayName || a.username || '未知';
  }

  userAvatar(obj) {
    const a = obj.attributes || obj || {};
    return a.avatarUrl || null;
  }

  avatarHtml(obj, size) {
    const name = this.userName(obj);
    const avatar = this.userAvatar(obj);
    const cls = 'TaroMentorList-card-avatar-placeholder' + (size === 'lg' ? ' large' : '');
    if (avatar) {
      return <img src={avatar} alt={name} />;
    }
    return <div className={cls}>{name.charAt(0).toUpperCase()}</div>;
  }

  statusBadge(status, terminationReason) {
    if (status === 'approved') return <span className="TaroMentorList-card-badge approved">进行中</span>;
    if (status === 'pending') return <span className="TaroMentorList-card-badge pending">申请中</span>;
    if (status === 'rejected') return <span className="TaroMentorList-card-badge rejected">已拒绝</span>;
    if (status === 'terminated') {
      if (terminationReason === 'graduation') return <span className="TaroMentorList-card-badge graduation">已毕业 🎓</span>;
      return <span className="TaroMentorList-card-badge terminated">已解除</span>;
    }
    return null;
  }

  // 等级元信息（与 CreditPage 一致的配色）
  levelMeta(level) {
    const studyColors = {
      'S0': { bg: '#F0F2F5', text: '#9EA7B0', accent: '#C4CBD3' },
      'S1': { bg: '#EEF2F7', text: '#4D698E', accent: '#4D698E' },
      'S2': { bg: '#E7EDF4', text: '#3F5A7E', accent: '#4D698E' },
      'S3': { bg: '#DFE7F1', text: '#324A6D', accent: '#4D698E' },
      'S4': { bg: '#D5DFEC', text: '#273B58', accent: '#4D698E' },
    };
    const certColors = {
      'C0': { bg: '#F4F4F4', text: '#9E9E9E', accent: '#BDBDBD' },
      'C1': { bg: '#F2EEE3', text: '#8A7340', accent: '#A89060' },
      'C2': { bg: '#EBE3D1', text: '#6E5C2C', accent: '#A89060' },
    };
    return studyColors[level] || certColors[level] || { bg: '#F0F2F5', text: '#9EA7B0', accent: '#C4CBD3' };
  }

  // 活跃状态元信息
  activityMeta(status) {
    const map = {
      active:   { label: '活跃',   color: '#3E7CB1', bg: '#E9F1F8' },
      recent:   { label: '近期活跃', color: '#B8902E', bg: '#FAF3E0' },
      inactive: { label: '不活跃', color: '#9E9E9E', bg: '#F4F4F4' },
      never:    { label: '未上线', color: '#9E9E9E', bg: '#F4F4F4' },
    };
    return map[status] || map.never;
  }

  // 成为师生时长（从 approvedAt 到现在的文字描述）
  durationText(dateStr) {
    if (!dateStr) return '';
    const start = new Date(dateStr);
    const now = new Date();
    const days = Math.floor((now - start) / 86400000);
    if (days < 1) return '今天';
    if (days < 30) return days + ' 天';
    const months = Math.floor(days / 30);
    const remDays = days % 30;
    if (months < 12) return months + ' 个月' + (remDays > 0 ? ' ' + remDays + ' 天' : '');
    const years = Math.floor(months / 12);
    const remMonths = months % 12;
    return years + ' 年' + (remMonths > 0 ? ' ' + remMonths + ' 个月' : '');
  }

  formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
  }

  // ─── 渲染（UserPage.content） ─────────────────────

  content() {
    if (this.loading) {
      return <LoadingIndicator />;
    }

    return (
      <div className="TaroMentorPage">
        <div className="TaroMentorPage-header">
          <h2 className="TaroMentorPage-title">师生关系</h2>
        </div>

        {/* 等级卡：导师等级 + 学员等级 */}
        {this.renderLevels()}

        {/* Tab 导航 */}
        <div className="TaroMentorPage-tabs">
          <button className={'Button' + (this.tab === 'myMentor' ? ' Button--primary' : '')}
            onclick={() => { this.tab = 'myMentor'; m.redraw(); }}>我的导师</button>
          <button className={'Button' + (this.tab === 'myStudents' ? ' Button--primary' : '')}
            onclick={() => { this.tab = 'myStudents'; m.redraw(); }}>我的学生</button>
        </div>

        <div className="TaroMentorPage-body">
          {this.tab === 'myMentor' && this.renderMyMentor()}
          {this.tab === 'myStudents' && this.renderMyStudents()}
        </div>
      </div>
    );
  }

  // ─── 等级卡 ─────────────────────────────────────────

  renderLevels() {
    const studyLevel = (this.creditData && this.creditData.studyLevel) || 'S0';
    const certLevel = (this.creditData && this.creditData.certLevel) || 'C0';
    const studyMeta = this.levelMeta(studyLevel);
    const certMeta = this.levelMeta(certLevel);

    return (
      <div className="TaroMentorPage-levels">
        <div className="TaroMentorPage-level">
          <div className="TaroMentorPage-level-icon" style={{ background: studyMeta.bg, color: studyMeta.text }}>
            <i className="fas fa-user-graduate"></i>
          </div>
          <div className="TaroMentorPage-level-info">
            <div className="TaroMentorPage-level-label">学员等级</div>
            <div className="TaroMentorPage-level-value" style={{ color: studyMeta.text }}>{studyLevel}</div>
          </div>
          <div className="TaroMentorPage-level-bar" style={{ background: studyMeta.accent }} />
        </div>
        <div className="TaroMentorPage-level">
          <div className="TaroMentorPage-level-icon" style={{ background: certMeta.bg, color: certMeta.text }}>
            <i className="fas fa-chalkboard-teacher"></i>
          </div>
          <div className="TaroMentorPage-level-info">
            <div className="TaroMentorPage-level-label">导师等级</div>
            <div className="TaroMentorPage-level-value" style={{ color: certMeta.text }}>{certLevel}</div>
          </div>
          <div className="TaroMentorPage-level-bar" style={{ background: certMeta.accent }} />
        </div>
      </div>
    );
  }

  // ─── Tab: 我的导师 ──────────────────────────────────

  renderMyMentor() {
    if (!this.myMentor) {
      return (
        <div>
          <div className="TaroMentorPage-placeholder">
            <p>你还没有绑定导师</p>
            <p className="TaroMentorPage-hint">前往下方选择一位导师建立师徒关系。</p>
          </div>
          {this.renderMentorList()}
        </div>
      );
    }

    const relId = this.myMentorRelation?.id;
    const bindTime = this.myMentorRelation?.approvedAt || this.myMentorRelation?.createdAt;

    return (
      <div className="TaroMentorPage-mySection">
        {/* 当前导师卡片 */}
        <div className="TaroMyMentor-card">
          <div className="TaroMyMentor-card-header">
            <div className="TaroMyMentor-card-avatar">{this.avatarHtml(this.myMentor, 'lg')}</div>
            <div>
              <h3>{this.userName(this.myMentor)}</h3>
              <div className="TaroMentorPage-meta" style="margin-top:6px;">
                <span className="TaroMentorPage-metaItem"><i className="fas fa-calendar-alt"></i> 拜师时间：{this.formatDate(bindTime)}</span>
                <span className="TaroMentorPage-metaItem"><i className="fas fa-clock"></i> 已相伴 {this.durationText(bindTime)}</span>
              </div>
            </div>
          </div>
          <div className="TaroMyMentor-card-status">
            {this.statusBadge('approved')}
          </div>
          <div className="TaroMyMentor-card-hint">
            你的导师将为你提供案例点评和指导。你可以在案例详情页提交解读后，等待导师点评。
          </div>
          {relId && (
            <div style="margin-top:16px;padding-top:14px;border-top:1px solid #eee;">
              <button
                className="Button Button--danger"
                disabled={this.actionLoading === relId}
                onclick={() => this.terminateRelation(relId)}
              >
                {this.actionLoading === relId ? '处理中...' : '解除师徒关系'}
              </button>
              <span style="margin-left:10px;font-size:12px;color:#999;">
                学生和导师均可单方面解除
              </span>
            </div>
          )}
        </div>

        {/* 曾经的导师历史（作为学生） */}
        {this.renderMentorHistory()}
      </div>
    );
  }

  renderMentorHistory() {
    const asStudent = this.history
      .filter((r) => {
        const a = r.attributes || r;
        return String(a.studentId) === String(this.user.id());
      })
      .filter((r) => {
        const a = r.attributes || r;
        return a.status !== 'approved' || String(a.mentorId) !== String(this.myMentor?.id);
      });

    if (!asStudent.length) return null;

    return (
      <div className="TaroMentorArchive-section" style="margin-top:24px;">
        <h4 className="TaroMentorArchive-section-title">曾经的导师</h4>
        {asStudent.map((rel) => {
          const a = rel.attributes || rel;
          const mentorData = rel.relationships?.mentor?.data || {};
          return (
            <div className="TaroMentorList-card" key={rel.id}>
              <div className="TaroMentorList-card-avatar">{this.avatarHtml(mentorData)}</div>
              <div className="TaroMentorList-card-info">
                <div className="TaroMentorList-card-name">{this.userName(mentorData)}</div>
                <div className="TaroMentorList-card-meta">
                  导师 · {this.formatDate(a.createdAt)}
                  {a.terminatedAt ? ' → ' + this.formatDate(a.terminatedAt) : ''}
                </div>
              </div>
              <div className="TaroMentorList-card-action">
                {this.statusBadge(a.status, a.terminationReason)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ─── 导师列表（未绑定时显示，可选导师拜师） ──────────

  renderMentorList() {
    if (!this.mentors.length) {
      return <div className="TaroMentorPage-placeholder"><p>暂无可用导师</p></div>;
    }

    return (
      <div className="TaroMentorList">
        <p className="TaroMentorPage-hint">选择一个导师建立师徒关系，导师将为你提供案例点评和指导。</p>
        {this.mentors.map((mentor) => {
          const isBound = this.myMentor && this.myMentor.id === mentor.id;
          const isRequested = this.myMentorRelation && this.myMentorRelation.status === 'pending';
          const attrs = mentor.attributes || mentor;
          const wandLevel = attrs.taroWandLevel || 'C0';
          const capacity = attrs.taroMentorCapacity || null;

          return (
            <div className={'TaroMentorList-card' + (isBound ? ' is-bound' : '')} key={mentor.id}>
              <div className="TaroMentorList-card-avatar">{this.avatarHtml(mentor)}</div>
              <div className="TaroMentorList-card-info">
                <div className="TaroMentorList-card-name">{this.userName(mentor)}</div>
                <div className="TaroMentorList-card-meta" style="font-size:12px;color:#888;margin-top:2px;">
                  <span style="margin-right:10px;">等级 <strong style="color:#8e44ad;">{wandLevel}</strong></span>
                  {capacity && (
                    <span>学员 {capacity.currentStudents}/{capacity.maxStudents}</span>
                  )}
                </div>
              </div>
              <div className="TaroMentorList-card-action">
                {isBound ? <span className="TaroMentorList-card-badge approved">已绑定</span>
                 : isRequested ? <span className="TaroMentorList-card-badge pending">申请中</span>
                 : <button className="Button Button--primary" disabled={this.sending === mentor.id}
                     onclick={() => this.requestMentor(mentor)}>
                     {this.sending === mentor.id ? '发送中...' : '拜师'}</button>}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ─── Tab: 我的学生 ──────────────────────────────────

  renderMyStudents() {
    return (
      <div className="TaroMentorPage-mySection">
        {/* 导师待办（仅导师可见） */}
        {this.isMentor && this.renderMentorTodo()}

        {/* 学生数量 + 学生档案列表 */}
        <div className="TaroMentorWorkbench-stats">
          <div className="TaroMentorWorkbench-stat-card">
            <div className="TaroMentorWorkbench-stat-value">{this.students.length}</div>
            <div className="TaroMentorWorkbench-stat-label">当前学生</div>
          </div>
        </div>

        {this.students.length === 0 ? (
          <div className="TaroMentorPage-placeholder">
            <p>{this.isMentor ? '你还没有学生' : '成为导师后可招收学生'}</p>
            {this.isMentor && <p className="TaroMentorPage-hint">学生拜师申请通过后，将显示在这里。</p>}
          </div>
        ) : (
          <div className="TaroMentorStudents">
            {this.students.map((item) => {
              const a = item.attributes || item;
              const student = a.student || item.student || {};
              const act = this.activityMeta(a.activityStatus);
              return (
                <div className="TaroMentorStudents-card" key={item.id || a.relationId}>
                  <div className="TaroMentorStudents-avatar">{this.avatarHtml(student)}</div>
                  <div className="TaroMentorStudents-info">
                    <div className="TaroMentorStudents-name">{this.userName(student)}</div>
                    <div className="TaroMentorStudents-meta">
                      <span><i className="fas fa-clock"></i> 已成为师生 {this.durationText(a.approvedAt)}</span>
                      {a.approvedAt && <span><i className="fas fa-calendar-alt"></i> {this.formatDate(a.approvedAt)} 拜师</span>}
                    </div>
                  </div>
                  <div className="TaroMentorStudents-activity" style={{ color: act.color, background: act.bg }}>
                    <i className="fas fa-circle" style="font-size:8px;margin-right:6px;"></i>{act.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 曾经的学员历史（作为导师） */}
        {this.renderStudentsHistory()}
      </div>
    );
  }

  renderStudentsHistory() {
    const asMentor = this.history
      .filter((r) => {
        const a = r.attributes || r;
        return String(a.mentorId) === String(this.user.id());
      })
      .filter((r) => {
        const a = r.attributes || r;
        return a.status !== 'approved' || !this.students.some((s) => {
          const sa = s.attributes || s;
          return String(sa.student?.id) === String(a.studentId);
        });
      });

    if (!asMentor.length) return null;

    return (
      <div className="TaroMentorArchive-section" style="margin-top:24px;">
        <h4 className="TaroMentorArchive-section-title">曾经的学生</h4>
        {asMentor.map((rel) => {
          const a = rel.attributes || rel;
          const studentData = rel.relationships?.student?.data || {};
          return (
            <div className="TaroMentorList-card" key={rel.id}>
              <div className="TaroMentorList-card-avatar">{this.avatarHtml(studentData)}</div>
              <div className="TaroMentorList-card-info">
                <div className="TaroMentorList-card-name">{this.userName(studentData)}</div>
                <div className="TaroMentorList-card-meta">
                  学生 · {this.formatDate(a.createdAt)}
                  {a.terminatedAt ? ' → ' + this.formatDate(a.terminatedAt) : ''}
                </div>
              </div>
              <div className="TaroMentorList-card-action">
                {this.statusBadge(a.status, a.terminationReason)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ─── 导师待办 ────────────────────────────────────────

  renderMentorTodo() {
    return (
      <div className="TaroMentorWorkbench">
        <div className="TaroMentorWorkbench-stats">
          <div className="TaroMentorWorkbench-stat-card">
            <div className="TaroMentorWorkbench-stat-value">{this.pendingReviews}</div>
            <div className="TaroMentorWorkbench-stat-label">待点评案例</div>
          </div>
          <div className="TaroMentorWorkbench-stat-card">
            <div className="TaroMentorWorkbench-stat-value">{this.pendingAnswers}</div>
            <div className="TaroMentorWorkbench-stat-label">待回答追问</div>
          </div>
        </div>

        {this.pendingRequests.length > 0 && (
          <div className="TaroMentorWorkbench-section">
            <h4 className="TaroMentorWorkbench-section-title">
              待审批的拜师申请（{this.pendingRequests.length}）
            </h4>
            {this.pendingRequests.map((rel) => {
              const a = rel.attributes || rel;
              const studentData = rel.relationships?.student?.data || {};
              return (
                <div className="TaroMentorWorkbench-request" key={rel.id}>
                  <div className="TaroMentorWorkbench-request-info">
                    <span className="TaroMentorWorkbench-request-name">{this.userName(studentData)}</span>
                    {a.message && <span className="TaroMentorWorkbench-request-message">留言：{a.message}</span>}
                    <span className="TaroMentorWorkbench-request-date">
                      申请于 {a.createdAt ? new Date(a.createdAt).toLocaleString() : '未知'}
                    </span>
                  </div>
                  <div className="TaroMentorWorkbench-request-actions">
                    <button className="Button Button--primary" disabled={this.actionLoading === rel.id}
                      onclick={() => this.approveRequest(rel.id)}>
                      {this.actionLoading === rel.id ? '处理中...' : '批准'}
                    </button>
                    <button className="Button Button--danger" disabled={this.actionLoading === rel.id}
                      onclick={() => this.rejectRequest(rel.id)}>拒绝</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="TaroMentorWorkbench-hint">
          <p>操作指引：</p>
          <ul>
            <li>在案例帖中打开手风琴面板提交导师点评</li>
            <li>在「追问」模块中回答学员追问</li>
          </ul>
        </div>
      </div>
    );
  }
}
